import { AppInitData } from '@/types/core';

export interface WriteupState {
	writeupObject: object;
}

export interface ContactRequest {
	firstName: string;
	lastName: string;
	phoneNumber?: string;
	email: string;
}

export interface AppointmentRequest {
	writeupId: number|undefined;
	type: number;
	date: string|object;
	time: string|object;
	customerAddress?: string;
	customerAddressNotes?: string;
}

export interface VehicleResponse {
	[key: string]: unknown;
	vin: string;
	dealerId: number;
	year: number;
	make: string;
	model: string;
	trim: string;
	stockNumber: string;
	price: number;
	reducedPrice: number;
	standardBody: string;
	mileage: number;
	engine: string;
	transmission: string;
	drivetrain: string;
	interiorColor: string;
	exteriorColor: string;
	epaCity: number;
	epaHighway: number;
	imageUrl: string;
	thumbImageUrl: string;
	images?: [];
	isNew: boolean;
	equipment?: [];
}

export enum DealType {
	lease = 'lease',
	loan = 'loan',
	cash = 'cash',
}

export enum protectionType {
	FAndI = 'fiexpress',
	Desking = 'desking'
}

export interface TermsObject {
	isDefault: boolean;
	dealNeeds: number;
	bankName: string;
	down: number;
	creditScore: number;
	apr: number;
	userApr: boolean;
	termLength: number;
	programMiles: number;
}

export interface RebatePayloadType {
	id: number | undefined;
	creditScore: number | undefined;
	downPayment: number | undefined;
	source: RebateSource | undefined;
}

export enum RebateSource {
	Manufacturer = 1,
	PriceRule = 2,
	DealerDiscount = 3,
	DealerPriceNegotiation = 4,
}

export interface RebateUpdateRequest {
	id: number;
}

// TODO: update with new API
export enum RebateCategory {
	Military = 'military',
	Loyalty = 'loyalty',
	College = 'college',
}

export interface RebateResponse {
	id:	number;
	expirationDate?: string;
	name?: string;
	description?: string;
	selected: boolean;
	optional: boolean;
	conditional: boolean;
	source: RebateSource;
	exclusions: number[];
	title?: string;
	cashValue: string;
	category: RebateCategory;
}

export interface WriteupResponse {
	writeupId: number;
	vehicle: VehicleResponse;
	tradeIn: {
		taxSavings: number;
		tradeinValue: number;
		plateNumber?: string;
		vin?: string;
		customerState?: string;
		year: number;
		make: string;
		model: string;
		trim: string;
		userValue: boolean;
		tradingIn: number;
		accident: boolean;
		rental: boolean;
		condition: string;
		isPaid: number;
		questionnaire: {
			accident: boolean;
			rental: boolean;
			cond: number;
			paid: boolean;
		};
		vehicle: {
			[key: string]: string;
		};
		amount: number;
		payoff: number;
		mileage: number;
	};
	dio: {
		title: string;
		price: number;
		isTaxable: boolean;
	};
	customerId: number;
	type: DealType;
	url: string;
	terms: {
		isDefault: boolean;
		dealNeeds: number;
		bankName: string;
		down: number;
		creditScore: number;
		apr: number;
		userApr: boolean;
		termLength: number;
		programMiles: number;
		providerCreditScore: number;
		isProviderCreditScoreSelected: boolean;
	};
	pricing: {
		listPrice: number;
		diaPrice: number;
		apr: number;
		tradeinValue: number;
		discounts: number;
		priceAdjustments: boolean;
		rebates: number;
		purchasePrice: number;
		taxes: number;
		fees: number;
		taxesRate: number;
		totalPrice: number;
		monthly: number;
		netCap: number;
		netResidual: number;
		protections: {
			serviceContract: number;
			other: number;
		};
		accessories: number;
		residualRate: number;
		leaseRate: number;
		leaseRateMoneyFactor: number;
	};
	contact: {
		firstName: string;
		lastName: string;
		phoneNumber: string;
		email: string;
		entityId: number;
		opportunityId: number;
	};
	dataAvailability: {
		incentives: boolean;
		rebates: boolean;
		fi: boolean;
		accessory: boolean;
	};
	leaseAvailable: boolean;
	selectedIncentives: [
		{
			title: string;
			name: string;
			description: string;
			selected: boolean;
			exclusions: [number];
			type: number;
			id: number;
			apr: number;
			terms: IncentiveTermMessage[];
			term: IncentiveTermMessage;
		}
	];
	zipCode: string;
	websiteId: number;
	createdDate: string;
	updatedDate: string;
}

export interface IncentiveTermMessage {
	apr: number;
	months: number;
	miles: number;
}

export interface IncentiveObject {
	id: number;
	terms: [
		{
			apr: number;
			months: number;
			miles: number;
		}
	];
	lenderName: string;
	name: string;
	description: string;
	exclusions: [number];
}

export interface IncentiveRequest {
	id?: number;
	term?: {
		apr: number;
		months: number;
		miles: number;
	};
}

export interface CompareDataObject {
	riskFrom: number;
	riskTo: number;
	programMiles: [number];
	terms: [
		{
			termLength: number;
			apr: number;
			lenderName: string;
		}
	];
}

export interface PaymentTermsDetails{
	termLength: number | undefined;
	apr: number | undefined;
	creditScore: number | undefined;
	userApr: boolean | undefined;
	down: number | undefined;
	programMiles: number | undefined;
}

export interface PaymentTermsResponse {
	down: {
		bottom: number;
		top: number;
	};
	score: {
		bottom: number;
		top: number;
	};
	termLength: [
		{
			value: number;
		}
	];
	compareData: CompareDataObject[];
}

export interface PaymentTermsPatchRequest {
	down: number;
	creditScore: number;
	apr: number;
	userApr: boolean;
	termLength: number;
	programMiles: number;
}

export interface WriteupRequest {
	site: {
		credentials: Partial<{
			websiteId: number;
			siteId: number;
			addressId: number;
		}>;
	};
	vehicle: {
		vin: string;
		sellingPrice?: number;
		imageUrl?: string;
	};
	tradeIn: {
		year?: number;
		make?: string;
		model?: string;
		trim?: string;
		style?: string;
		amount: number;
		payoff: number;
		mileage?: number;
	};
	dio: [
		{
			title: string;
			price: number;
			isTaxable: boolean;
		}
	];
	customer: Partial<{
		entityId: number;
		opportunityId: number;
		contact: {
			firstName: string;
			lastName: string;
			phoneNumber?: string;
			email: string;
		};
	}>;
	scope: writeupScope;
	type: writeupType;
	zipCode: string;
}

export interface TradeInRequest {
	amount: number;
	tradingIn?: boolean;
	userValue?: boolean;
	year?: string;
	make?: string;
	model?: string;
	trim?: string;
	mileage?: number;
	customerState?: string;
	plateNumber?: string;
	condition?: string;
	isPaid?: number;
	payoff?: number;
	taxSavings?: number;
	vehicle?: TradeInServiceVehicleRequest;
}

export interface TradeInServiceVehicleRequest {
	id: number;
	year: number;
	make: string;
	model: string;
	trim: string;
	body: string;
	doors: string;
	engine: string;
	drivetrain: string;
	fuelType: string;
}

export interface TradeInResponse {
	userValue: true;
	tradingIn: 0;
	questionnaire: {
		accident: true;
		rental: true;
		cond: 0;
		paid: true;
	};
	amount: 0;
	payoff: 0;
	mileage: 0;
	taxSavings: 0;
	tradeInValue: 0;
	year: 0;
	make: string;
	model: string;
	trim: string;
	drilldown: {
		year: {
			selected: 0;
			values: [
				{
					key: string;
					value: string;
				}
			];
		};
		make: {
			selected: string;
			values: [
				{
					key: string;
					value: string;
				}
			];
		};
		model: {
			selected: string;
			values: [
				{
					key: string;
					value: string;
				}
			];
		};
		trim: {
			selected: string;
			values: [
				{
					key: string;
					value: string;
				}
			];
		};
		series: {
			selected: string;
			values: [
				{
					key: string;
					value: string;
				}
			];
		};
		style: {
			selected: string;
			values: [
				{
					key: string;
					value: string;
				}
			];
		};
		engine: {
			selected: string;
			values: [
				{
					key: string;
					value: string;
				}
			];
		};
		drivetrain: {
			selected: string;
			values: [
				{
					key: string;
					value: string;
				}
			];
		};
		transmission: {
			selected: string;
			values: [
				{
					key: string;
					value: string;
				}
			];
		};
	};
}

export interface FiTermRequest {
	months?: number | null;
	miles?: number | null;
	coverage?: string;
	deductible?: string | number;
	price?: number;
}

export interface FiRequest {
	type: number;
	id?: number;
	term?: FiTermRequest;
}

export interface TermListObject {
	optionId: number|undefined;
	fiTermsResponse: Array<FiTerm>;
}

export interface AllCoveragesObject {
	optionId: number|undefined;
	coverages: Array<CoverageDropdownItem>;
}

export interface UpdateOptionPayload {
	optionId: number;
	type: number;
	selectedCoverage?: string;
	selectedDeductible?: string | number;
	months?: number | null;
	price?: number;
	miles?: number | null;
}

export interface FiResponse {
	type: number;
	id: number;
	term?: FiTermRequest;
	title?: string;
	description?: string;
	brochure?: string;
	videoUrl?: string;
	image?: string;
	price: number;
	totalPrice?: number;
	selected?: boolean;
	externalType?: string;
}

export interface AccessoryRequest {
	id: number;
}

export interface AccessoryResponse {
	id: number;
	accessoryId: number;
	writeupId: number;
	accessoryCode?: string;
	name?: string;
	description?: string;
	price: number;
	selected?: boolean;
	imageUrl?: string;
	disclaimer?: string;
	startDate?: string;
	endDate?: string;
}

export interface FiTerm {
	price: number;
	coverage: string;
	miles: number|null;
	months: number|null;
	deductible: string|null;
	isTotal?: boolean;
}

interface DropdownItem {
	value: string|number;
	label: string;
}

interface TermDropdownItem extends DropdownItem {
	price: number;
	months: number|null;
	miles: number|null;
}

interface DeductibleDropdownItem extends DropdownItem {
	terms: Array<TermDropdownItem>;
}

export interface CoverageDropdownItem extends DropdownItem {
	deductible: Array<DeductibleDropdownItem>;
}

export interface ScheduleTestDriveData {
	writeupId: number|undefined;
	type: number;
	date: string|object;
	time: string|object;
	customerAddress: string;
	customerAddressNotes: string;
}

export interface DatesAppointmentData {
	weekStartDate: number;
	startDate: string;
	endDate: string;
	disabledDates?: object;
}

export interface TimeslotsAppointmentData {
	isAvailable: boolean;
	timeslots: Array<TimeslotsItem>;
}

interface TimeslotsItem {
	startTime: string;
	startTimeLabel: string;
	endTime: string;
	endTimeLabel: string;
	isBusy: boolean;
}

export interface Timeslots {
	label: string;
	disabled: boolean;
}

export interface TestDriveAppointmentData {
	date: number;
	time: string;
}

export interface AppointmentResponseData {
	appointmentId: null | number | undefined;
	type: number;
	date: string;
	time: string;
	error: null | string;
	success: boolean;
	isBusy: boolean;
}

export type writeupScope = 'customer'|'role'|'site';

export type writeupType = 'loan'|'lease'|'cash';

export interface Tax {
	id: string|null|number;
	name: string|null|number;
	amount: number|null|string;
}

export interface Fee extends Tax {
	description: string|null;
}

export interface TaxesAndFees {
	taxes: Array<Tax>;
	fees: Array<Fee>;
}

export interface ShiftDigitalData {
	dealId?: string;
	clientId: string;
	retailerId: string;
	providerId: string;
	vehicleYear: number;
	vehicleMake: string;
	vehicleModel: string;
	vehicleVin: string;
	vehicleStatus: string;
	jsScriptUrl: string;
	sessionId?: string;
}

export interface GtmData {
	dealerId: string;
	providerId: string;
	containerId: string;
	siteSection: string;
	eventsMapping: {
		initialView: {
			event: string;
			DRSType: string;
		};
		paymentCalculator: {
			event: string;
			DRSType: string;
		};
		testDrive: {
			event: string;
			DRSType: string;
		};
		tradeIn: {
			event: string;
			DRSType: string;
		};
	};
	dataLayer: [
		DataLayerOne,
		DataLayerTwo
	];
	formMapping: {
		digitalRetailTradeInResponsive1: string;
		digitalRetailScheduleTestdriveResponsive1: string;
		digitalRetailAdditionalSavingResponsive1: string;
		digitalRetailCompletedDealResponsive1: string;
		digitalRetailGetAssistanceEmailResponsive1: string;
		digitalRetailSaveAndEmailResponsive1: string;
		digitalRetailSaveAndTextResponsive1: string;
	};
}

export interface DataLayerOne {
	'event': string;
	'hform': string | undefined;
	'formclass': string;
	'Dealerid': string;
}

export interface DataLayerTwo {
	'content-group': string;
	'DealerId': string;
	'ProviderID': string;
	'isNew': number;
	'certified': number;
	'vehicleModel': string;
	'vehicleVin': string;
}

export interface TealiumResponse {
	dataLayer: {
		site_section: string;
		site_sub_section: string;
		page_name?: string;
		layout_type?: string;
		page_url?: string;
		current_time: string;
	};
	linksMapping: object;
	jsAsyncScriptUrl: string;
	formMapping: object;
	pageMapping: object;
}

export interface TealiumRequest {
	pageMapping: string;
	linkMapping?: string;
	formMapping?: string;
	initData?: AppInitData | null;
}

export interface TealiumEventsData {
	events: object;
	tealiumDataLayer: object;
	utag_event: string;
}
